import { queryOptions, useMutation } from '@tanstack/react-query'

import {
  ContactRequest as APIContactRequest,
  LegalEntityResponseV3 as APILegalEntity,
  OrganizationRequest,
  OrganizationResponse,
  PagedContactsResponse,
  PagedOrganizationResponse as APIOrganizationResponse,
  UpdateOrganizationRequest,
  VatResponse as APIVatResponse,
} from '@forto-dev/customer-master-data-models'

import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'

import { Contacts, ContactsSchema } from '../domain/contact'
import { LegalEntities, LegalEntitiesSchema, RequestLegalEntitySchemaType } from '../domain/legalEntity'
import { BINNumberSchema } from '../domain/organization'
import { Organization, Organizations, OrganizationSchema, OrganizationsSchema } from '../domain/organization'
import { VatValidationData, VatValidationDataSchema } from '../domain/vat'

const basePath = '/api/customer-master-data'
const shippingBasePath = '/api/shipping'

export const ORGANIZATIONS_QUERY_KEY = 'organizations'
export const CONTACTS_QUERY_KEY = 'contacts'

export type PaginationResponse<T> = {
  items: T[]
  total: number
}

type LegalEntityRequest = RequestLegalEntitySchemaType & {
  customerId: string
  organizationId: string
  billingEntityId: string
  hasVat: boolean
}

export const masterDataConfig: ApiConfig<{
  getOrganizations: EndpointConfig<string, APIOrganizationResponse, Organizations>
  updateOrganization: EndpointConfig<{ id: string; updatedOrg: UpdateOrganizationRequest }, void>
  createOrganization: EndpointConfig<OrganizationRequest, OrganizationResponse, Organization>
  deleteOrganization: EndpointConfig<{ id: string; customerId: string }, void>
  getLegalEntities: EndpointConfig<string, PaginationResponse<APILegalEntity>, LegalEntities>
  requestLegalEntity: EndpointConfig<LegalEntityRequest, void>
  validateVAT: EndpointConfig<string, APIVatResponse, VatValidationData>
  getContacts: EndpointConfig<{ organizationId: string; customerId: string }, PagedContactsResponse, Contacts>
  createContact: EndpointConfig<APIContactRequest, void>
  updateContact: EndpointConfig<{ id: string; contactRequest: APIContactRequest }, void>
  deleteContact: EndpointConfig<{ id: string; customerId: string }, void>
  revealBinNumber: EndpointConfig<
    { organizationId: string; customerId: string; accountType: string; isRevealed: boolean },
    { binNumber: string }
  >
}> = {
  getOrganizations: {
    requestConfig: (customerID) => ({
      endpoint: `${basePath}/customer-core/organizations?customerId=${customerID}`,
      method: 'get',
      deserializationStrategy: OrganizationsSchema,
    }),
  },
  updateOrganization: {
    requestConfig: ({ id, updatedOrg }) => ({
      endpoint: `${basePath}/customer-core/organizations/${id}?customerId=${updatedOrg.customerId}`,
      method: 'put',
      body: updatedOrg,
    }),
  },
  createOrganization: {
    requestConfig: (organization) => ({
      endpoint: `${basePath}/customer-core/organizations`,
      method: 'post',
      body: organization,
      deserializationStrategy: OrganizationSchema,
    }),
  },
  deleteOrganization: {
    requestConfig: ({ id, customerId }) => ({
      endpoint: `${basePath}/customer-core/organizations/${id}?customerId=${customerId}`,
      method: 'delete',
    }),
  },
  getLegalEntities: {
    requestConfig: (organizationId) => ({
      endpoint: `${basePath}/v3/legal-entities?organizationId=${organizationId}`,
      method: 'get',
      deserializationStrategy: LegalEntitiesSchema,
    }),
  },
  requestLegalEntity: {
    requestConfig: (legalEntityRequest: LegalEntityRequest) => ({
      endpoint: `${shippingBasePath}/legalEntities`,
      method: 'post',
      body: legalEntityRequest,
    }),
  },
  validateVAT: {
    requestConfig: (vatNumber) => ({
      endpoint: `${basePath}/vat/validation?vatID=${vatNumber}`,
      method: 'get',
      deserializationStrategy: VatValidationDataSchema,
    }),
  },
  getContacts: {
    requestConfig: ({ organizationId, customerId }) => ({
      endpoint: `${basePath}/customer-core/contacts/organization/${organizationId}`,
      method: 'get',
      deserializationStrategy: ContactsSchema,
      options: {
        params: {
          customerId,
        },
      },
    }),
  },
  createContact: {
    requestConfig: (contactRequest) => ({
      endpoint: `${basePath}/customer-core/contacts`,
      method: 'post',
      body: contactRequest,
    }),
  },
  updateContact: {
    requestConfig: ({ id, contactRequest }) => ({
      endpoint: `${basePath}/customer-core/contacts/${id}?customerId=${contactRequest.customerId}`,
      method: 'put',
      body: contactRequest,
    }),
  },
  deleteContact: {
    requestConfig: ({ id, customerId }) => ({
      endpoint: `${basePath}/customer-core/contacts/${id}?customerId=${customerId}`,
      method: 'delete',
    }),
  },
  revealBinNumber: {
    requestConfig: ({ organizationId, customerId, accountType }) => ({
      endpoint: `${basePath}/customer-core/organizations/${organizationId}/customs-settings/bin?account=${accountType}&customerId=${customerId}`,
      method: 'get',
      deserializationStrategy: BINNumberSchema,
    }),
  },
}

const masterDataClient = clientFactory(masterDataConfig)

export const organizationsQueryOptions = (customerID?: string) => {
  return queryOptions({
    queryKey: [ORGANIZATIONS_QUERY_KEY, customerID],
    queryFn: () => masterDataClient.getOrganizations(customerID || ''),
    enabled: !!customerID,
  })
}

export const useUpdateOrganization = () => {
  return useMutation({
    mutationFn: masterDataClient.updateOrganization,
  })
}

export const useCreateOrganization = () => {
  return useMutation({
    mutationFn: masterDataClient.createOrganization,
  })
}

export const useDeleteOrganization = () => {
  return useMutation({
    mutationFn: masterDataClient.deleteOrganization,
  })
}

export const useValidateVAT = () => {
  return useMutation({
    mutationFn: masterDataClient.validateVAT,
  })
}

export const legalEntitiesQueryOptions = (organizationId: string) => {
  return queryOptions({
    queryKey: ['legalEntities', organizationId],
    queryFn: () => masterDataClient.getLegalEntities(organizationId),
    enabled: !!organizationId,
  })
}

export const useRequestLegalEntity = () => {
  return useMutation({
    mutationFn: masterDataClient.requestLegalEntity,
  })
}

export const contactsQueryOptions = (organizationId: string, customerId: string) => {
  return queryOptions({
    queryKey: [CONTACTS_QUERY_KEY, organizationId, customerId],
    queryFn: () => masterDataClient.getContacts({ organizationId, customerId }),
  })
}

export const useCreateContact = () => {
  return useMutation({
    mutationFn: masterDataClient.createContact,
  })
}

export const useUpdateContact = () => {
  return useMutation({
    mutationFn: masterDataClient.updateContact,
  })
}

export const useDeleteContact = () => {
  return useMutation({
    mutationFn: masterDataClient.deleteContact,
  })
}

export const binNumberQueryOptions = (
  organizationId: string,
  customerId: string,
  accountType: string,
  isRevealed: boolean
) => {
  return queryOptions({
    queryKey: ['revealBinNumber', { organizationId, customerId, accountType, isRevealed }],
    queryFn: () => masterDataClient.revealBinNumber({ organizationId, customerId, accountType, isRevealed }),
  })
}
